import {z} from 'zod';
import {FC, useState} from 'react';
import {toast} from 'react-toastify';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {
  addBrandEmailDomain,
  deleteBrand,
  getBrand,
  removeBrandEmailDomain,
  updateBrand,
  updateBrandLogo,
  updateBrandThumbnail,
  verifyBrandEmailDomain,
  VerdocsEndpoint,
  type IBrand,
} from '@verdocs/js-sdk';
import {ColorPicker, FlexFill, FormColumn, FormColumns, FormLabel, HelpDialog, PageTitle, PhotoSelector, Spinner, Tabs, TextInput} from '../../Components';
import {invalidateQuery} from '../../react-sdk/queries';
import {OkCancelDialog} from '../../Dialogs/OkCancelDialog';
import {AppState} from '../../AppState';
import {ArrowLeftIcon, ClipboardDocumentIcon} from '@heroicons/react/24/outline';
import {CheckCircleIcon, XCircleIcon} from '@heroicons/react/24/solid';
import {BrandPreview} from './BrandPreview';

const TABS = [
  {id: 'branding', label: 'Branding'},
  {id: 'styling', label: 'Styling'},
  {id: 'domain', label: 'Custom Domain'},
  {id: 'email', label: 'Sender Email'},
  {id: 'documents', label: 'Documents'},
  {id: 'preview', label: 'Preview'},
  {id: 'settings', label: 'Settings'},
];

const schema = z.object({
  name: z.string().trim().max(100).optional().or(z.literal('')),
  page_title: z.string().trim().max(100).optional().or(z.literal('')),
  primary_color: z.string().trim().optional(),
  secondary_color: z.string().trim().optional(),
  full_logo_url: z.string().trim().optional(),
  thumbnail_url: z.string().trim().optional(),
  favicon_url: z.string().trim().optional().or(z.literal('')),
  powered_by_label: z.string().trim().optional().or(z.literal('')),
  powered_by_url: z.string().trim().optional().or(z.literal('')),
  terms_use_url: z.string().trim().optional().or(z.literal('')),
  privacy_policy_url: z.string().trim().optional().or(z.literal('')),
  support_contact: z.string().trim().optional().or(z.literal('')),
  style_overrides: z.string().optional().or(z.literal('')),
  pdf_signature_reason: z.string().trim().optional().or(z.literal('')),
  pdf_signature_location: z.string().trim().optional().or(z.literal('')),
  disclaimer: z.string().trim().optional().or(z.literal('')),
});

type SchemaType = z.infer<typeof schema>;

const emailDomainSchema = z.object({
  subdomain: z
    .string()
    .trim()
    .min(3)
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, 'Must be a valid domain (e.g. acme.com or notify.acme.com)'),
  local_part: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9._-]*$/i, 'Letters, numbers, dots, hyphens, underscores'),
  display_name: z.string().trim().max(128).optional().or(z.literal('')),
  reply_to: z.string().trim().email('Must be a valid email').optional().or(z.literal('')),
});

const getEditableFields = (brand?: IBrand) => ({
  name: brand?.name || '',
  page_title: brand?.page_title || '',
  primary_color: brand?.primary_color || '',
  secondary_color: brand?.secondary_color || '',
  full_logo_url: brand?.full_logo_url || '',
  thumbnail_url: brand?.thumbnail_url || '',
  favicon_url: brand?.favicon_url || '',
  powered_by_label: brand?.powered_by_label || '',
  powered_by_url: brand?.powered_by_url || '',
  terms_use_url: brand?.terms_use_url || '',
  privacy_policy_url: brand?.privacy_policy_url || '',
  support_contact: brand?.support_contact || '',
  style_overrides: brand?.style_overrides || '',
  pdf_signature_reason: brand?.pdf_signature_reason || '',
  pdf_signature_location: brand?.pdf_signature_location || '',
  disclaimer: brand?.disclaimer || '',
});

const StatusBadge: FC<{status: string | null | undefined}> = ({status}) => {
  if (!status) return <span className="text-gray-400 italic text-xs">Not configured</span>;
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    verified: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    suspended: 'bg-gray-100 text-gray-800',
  };
  return <span className={`${styles[status] || 'bg-gray-100 text-gray-800'} text-xs font-medium px-2.5 py-0.5 rounded`}>{status}</span>;
};

const VerificationIcon: FC<{verified: boolean}> = ({verified}) =>
  verified ? <CheckCircleIcon className="size-5 text-green-600" /> : <XCircleIcon className="size-5 text-gray-300" />;

const DnsRecord: FC<{type: string; host: string; value: string}> = ({type, host, value}) => {
  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-md border border-slate-200 dark:border-slate-600 mb-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-500 uppercase">{type} Record</span>
        <button
          onClick={() => copy(value, 'Value')}
          className="text-gray-400 hover:text-[#654dcb] flex items-center gap-1 text-xs"
          title="Copy value">
          <ClipboardDocumentIcon className="size-4" />
          Copy Value
        </button>
      </div>
      <div className="flex items-center text-xs text-slate-500 mb-1">
        Host:&nbsp;<span className="font-mono select-all">{host}</span>
        <button
          onClick={() => copy(host, 'Host')}
          className="text-gray-300 hover:text-[#654dcb] ml-1"
          title="Copy host">
          <ClipboardDocumentIcon className="size-3.5" />
        </button>
      </div>
      <div className="font-mono text-sm bg-white dark:bg-slate-900 p-2 border rounded select-all break-all">{value}</div>
    </div>
  );
};

interface BrandDetailProps {
  brandId: string;
  onBack: () => void;
}

export const BrandDetail: FC<BrandDetailProps> = ({brandId, onBack}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const [activeTab, setActiveTab] = useState('branding');
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [newThumbnail, setNewThumbnail] = useState<File | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [working, setWorking] = useState(false);
  const [brand, setBrand] = useState<IBrand | null>(null);

  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [emailSubdomain, setEmailSubdomain] = useState('');
  const [emailLocalPart, setEmailLocalPart] = useState('');
  const [emailDisplayName, setEmailDisplayName] = useState('');
  const [emailReplyTo, setEmailReplyTo] = useState('');
  const [confirmingRemoveEmail, setConfirmingRemoveEmail] = useState(false);

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () =>
      getBrand(VerdocsEndpoint.getDefault(), organizationId, brandId).then((b) => {
        setBrand(b);
        return getEditableFields(b);
      }),
  });

  const onSubmit = async (data: SchemaType) => {
    setWorking(true);
    try {
      const updated = await updateBrand(VerdocsEndpoint.getDefault(), organizationId, brandId, {
        name: data.name?.trim() || null,
        page_title: data.page_title?.trim() || null,
        primary_color: data.primary_color?.trim() || null,
        secondary_color: data.secondary_color?.trim() || null,
        full_logo_url: data.full_logo_url?.trim() || null,
        thumbnail_url: data.thumbnail_url?.trim() || null,
        favicon_url: data.favicon_url?.trim() || null,
        powered_by_label: data.powered_by_label?.trim() || null,
        powered_by_url: data.powered_by_url?.trim() || null,
        terms_use_url: data.terms_use_url?.trim() || null,
        privacy_policy_url: data.privacy_policy_url?.trim() || null,
        support_contact: data.support_contact?.trim() || null,
        style_overrides: data.style_overrides?.trim() || null,
        pdf_signature_reason: data.pdf_signature_reason?.trim() || null,
        pdf_signature_location: data.pdf_signature_location?.trim() || null,
        disclaimer: data.disclaimer?.trim() || null,
      });
      setBrand(updated);
      form.reset(getEditableFields(updated));
      invalidateQuery(['brands']);
      toast.success('Brand updated');
    } catch (e: any) {
      toast.error(`Error updating brand: ${e.response?.data?.error || e.message}`);
    }

    if (newLogo) {
      try {
        const updated = await updateBrandLogo(VerdocsEndpoint.getDefault(), organizationId, brandId, newLogo);
        setBrand(updated);
        setNewLogo(null);
        toast.success('Logo uploaded');
      } catch (e: any) {
        toast.error(`Error uploading logo: ${e.response?.data?.error || e.message}`);
      }
    }

    if (newThumbnail) {
      try {
        const updated = await updateBrandThumbnail(VerdocsEndpoint.getDefault(), organizationId, brandId, newThumbnail);
        setBrand(updated);
        setNewThumbnail(null);
        toast.success('Thumbnail uploaded');
      } catch (e: any) {
        toast.error(`Error uploading thumbnail: ${e.response?.data?.error || e.message}`);
      }
    }

    setWorking(false);
  };

  const handleDelete = async () => {
    setWorking(true);
    try {
      await deleteBrand(VerdocsEndpoint.getDefault(), organizationId, brandId);
      invalidateQuery(['brands']);
      toast.success('Brand deleted');
      onBack();
    } catch (e: any) {
      toast.error(`Error deleting brand: ${e.response?.data?.error || e.message}`);
    }
    setConfirmingDelete(false);
    setWorking(false);
  };

  const handleAddEmailDomain = async () => {
    const result = emailDomainSchema.safeParse({
      subdomain: emailSubdomain,
      local_part: emailLocalPart,
      display_name: emailDisplayName,
      reply_to: emailReplyTo,
    });
    if (!result.success) {
      toast.error(result.error.issues[0]?.message || 'Invalid input');
      return;
    }
    setWorking(true);
    try {
      const updated = await addBrandEmailDomain(VerdocsEndpoint.getDefault(), organizationId, brandId, {
        subdomain: result.data.subdomain,
        local_part: result.data.local_part,
        display_name: result.data.display_name || undefined,
        reply_to: result.data.reply_to || undefined,
      });
      setBrand(updated);
      setShowEmailSetup(false);
      invalidateQuery(['brands']);
      toast.success('Email domain configured');
    } catch (e: any) {
      toast.error(`Error configuring email domain: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const handleRemoveEmailDomain = async () => {
    setWorking(true);
    try {
      const updated = await removeBrandEmailDomain(VerdocsEndpoint.getDefault(), organizationId, brandId);
      setBrand(updated);
      setConfirmingRemoveEmail(false);
      invalidateQuery(['brands']);
      toast.success('Email domain removed');
    } catch (e: any) {
      toast.error(`Error removing email domain: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const handleVerifyEmailDomain = async () => {
    setWorking(true);
    try {
      const updated = await verifyBrandEmailDomain(VerdocsEndpoint.getDefault(), organizationId, brandId);
      setBrand(updated);
      invalidateQuery(['brands']);
      const allVerified = updated.email_spf_verified && updated.email_dkim_verified && updated.email_dmarc_verified;
      if (allVerified) {
        toast.success('All DNS records verified — your custom email domain is now active.');
      } else {
        const pending = [];
        if (!updated.email_spf_verified) pending.push('SPF');
        if (!updated.email_dkim_verified) pending.push('DKIM');
        if (!updated.email_dmarc_verified) pending.push('DMARC');
        toast.info(`${pending.join(', ')} not yet verified. DNS changes can take up to 48 hours to propagate.`);
      }
    } catch (e: any) {
      toast.error(`Error verifying domain: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  if (form.formState.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner mode="dark" />
      </div>
    );
  }

  const dirty = form.formState.isDirty || !!newLogo || !!newThumbnail;

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#654dcb] hover:underline mb-4 self-start">
        <ArrowLeftIcon className="size-4" />
        Back to Brands
      </button>

      <PageTitle>
        {brand?.name || brand?.key || 'Brand'}
        <span className="ml-3 text-sm font-mono text-gray-400 self-center">{brand?.key}</span>
        <FlexFill />
      </PageTitle>

      <Tabs tabs={TABS} activeTab={activeTab} onSelect={setActiveTab} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {/* ── Branding Tab ── */}
          {activeTab === 'branding' && (
            <div className="pt-4">
              <FormColumns>
                <FormColumn>
                  <TextInput label="Brand Name" name="name" placeholder="Defaults to organization name" />
                  <TextInput label="Page Title" name="page_title" placeholder="Defaults to brand name" />
                  <TextInput label="Support Contact" name="support_contact" placeholder="e.g. support@acme.com" />

                  <FormLabel className="flex flex-row w-full mt-4">
                    <div>Powered By</div>
                    <FlexFill />
                    <HelpDialog title="Powered By Branding">
                      <p className="mb-4">
                        If set, a "Powered by" label is shown in the lower-left corner of the signing experience. If empty, it is hidden.
                      </p>
                      <p>If the URL is also set, the label becomes a clickable link that opens in a new tab.</p>
                    </HelpDialog>
                  </FormLabel>
                  <TextInput label="Label" name="powered_by_label" />
                  <TextInput label="URL" name="powered_by_url" />
                </FormColumn>

                <FormColumn>
                  <FormLabel className="flex flex-row w-full">
                    <div>Footer Links</div>
                    <FlexFill />
                    <HelpDialog title="Footer Links">
                      <p>Optional URLs for Terms of Service and Privacy Policy. These are shown as clickable links in the signing footer.</p>
                    </HelpDialog>
                  </FormLabel>
                  <TextInput label="Terms of Use URL" name="terms_use_url" />
                  <TextInput label="Privacy Policy URL" name="privacy_policy_url" />
                  <TextInput label="Favicon URL" name="favicon_url" placeholder="URL to a 32x32 PNG" />
                </FormColumn>
              </FormColumns>
            </div>
          )}

          {/* ── Styling Tab ── */}
          {activeTab === 'styling' && (
            <div className="pt-4">
              <FormColumns>
                <FormColumn className="items-start">
                  <FormLabel className="flex flex-row w-full">
                    <div>Logo</div>
                    <FlexFill />
                    <HelpDialog title="Logo and Thumbnail">
                      <p className="mb-4">The logo is shown at the top of email templates and in rectangular regions.</p>
                      <p className="mb-4">The thumbnail is used in smaller areas where a square format is more suitable.</p>
                      <p>If not provided, Verdocs logos are used instead.</p>
                    </HelpDialog>
                  </FormLabel>
                  <PhotoSelector newFile={newLogo} value={form.getValues().full_logo_url} onSelectFile={setNewLogo} />

                  <FormLabel className="mt-4">Thumbnail</FormLabel>
                  <PhotoSelector newFile={newThumbnail} value={form.getValues().thumbnail_url} onSelectFile={setNewThumbnail} />
                </FormColumn>

                <FormColumn className="items-start">
                  <FormLabel className="flex flex-row w-full">
                    <div>Primary Color</div>
                    <FlexFill />
                    <HelpDialog title="Brand Colors">
                      <p className="mb-4">
                        The primary color is used as a background in emails and for theming. Text on this color is white, so choose a dark
                        shade.
                      </p>
                      <p>The secondary color is used for buttons and accents. It should complement the primary color.</p>
                    </HelpDialog>
                  </FormLabel>
                  <ColorPicker name="primary_color" defaultValue="#654dcb" />

                  <FormLabel className="mt-4">Secondary Color</FormLabel>
                  <ColorPicker name="secondary_color" defaultValue="#55bc81" />
                </FormColumn>
              </FormColumns>

              <div className="flex flex-row items-center mt-6 mb-4">
                <h3 className="text-lg font-semibold text-[#33364b] dark:text-white">CSS Overrides</h3>
                <FlexFill />
                <HelpDialog title="CSS Overrides">
                  <p className="mb-4">
                    Enter custom CSS to override the default signing experience styles. Use <code>--verdocs-*</code> CSS variables to customize
                    colors, borders, and other visual properties.
                  </p>
                  <p>CSS is sanitized on save to prevent security issues. External imports and JavaScript expressions are not allowed.</p>
                </HelpDialog>
              </div>

              <textarea
                {...form.register('style_overrides')}
                rows={16}
                className="font-mono text-sm p-3 w-full border rounded-lg border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                placeholder={`/* Custom CSS overrides */\n:root {\n  --verdocs-theme-primary-color: #654dcb;\n  --verdocs-theme-secondary-color: #55bc81;\n}`}
              />
            </div>
          )}

          {/* ── Custom Domain Tab ── */}
          {activeTab === 'domain' && (
            <div className="pt-4 max-w-2xl">
              <p className="text-sm text-gray-500 mb-4">
                Serve the Verdocs signing experience under your own subdomain. Signing links, login pages, and documents will appear on your
                domain instead of verdocs.com.
              </p>

              {brand?.app_domain ? (
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-mono text-sm">{brand.app_domain}</span>
                    <StatusBadge status={brand.app_domain_status} />
                  </div>
                  {brand.app_domain_dcv_token && (
                    <>
                      <p className="text-sm text-gray-500 mb-3">Add these DNS records to activate your custom domain:</p>
                      <DnsRecord type="CNAME" host={brand.app_domain} value="custom.verdocs.com" />
                      <DnsRecord
                        type="CNAME (DCV)"
                        host={`_acme-challenge.${brand.app_domain}`}
                        value={`${brand.app_domain}.${brand.app_domain_dcv_token}.dcv.cloudflare.com`}
                      />
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Custom domain support is coming soon. You'll be able to add a CNAME record (e.g. <code>sign.yourcompany.com</code>) to
                    serve Verdocs under your own subdomain.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Sender Email Tab ── */}
          {activeTab === 'email' && (
            <div className="pt-4 max-w-2xl">
              <p className="text-sm text-gray-500 mb-4">
                Send notifications from your own email address. Recipients see emails from your brand, not verdocs.com. You'll verify
                ownership of a domain via DNS records (SPF, DKIM, and DMARC), then all notifications for this brand will be sent from that
                domain.
              </p>

              {brand?.email_domain ? (
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold">
                      {brand.email_local_part}@{brand.email_domain}
                    </span>
                    <StatusBadge status={brand.email_domain_status} />
                  </div>
                  {brand.email_display_name && <p className="text-sm text-gray-500 mb-1">Display name: {brand.email_display_name}</p>}
                  {brand.email_reply_to && (
                    <p className="text-sm text-gray-500 mb-3">
                      Reply-to: {brand.email_reply_to}
                      {brand.email_reply_to_verified ? (
                        <span className="ml-2 text-green-600 text-xs font-medium">verified</span>
                      ) : (
                        <span className="ml-2 text-yellow-600 text-xs font-medium">unverified</span>
                      )}
                    </p>
                  )}

                  {brand.email_spf_verified && brand.email_dkim_verified && brand.email_dmarc_verified ? (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                      <p className="text-sm text-green-800 dark:text-green-300 font-medium">
                        All DNS records verified — your custom email domain is active.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                      <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
                        DNS verification incomplete
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        Add the records below in your DNS provider, then click Verify DNS Records. Changes typically propagate
                        within 15–60 minutes, though some providers may take up to 48 hours.
                      </p>
                    </div>
                  )}

                  <div className="mb-6">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Verification Status</p>
                    <div className="flex flex-col gap-2 mb-3">
                      <div className="flex items-center gap-2 text-sm">
                        <VerificationIcon verified={brand.email_spf_verified} />
                        <span className="font-medium w-14">SPF</span>
                        <span className="text-gray-400 text-xs">
                          {brand.email_spf_verified ? 'Verified' : 'Add the TXT record below'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <VerificationIcon verified={brand.email_dkim_verified} />
                        <span className="font-medium w-14">DKIM</span>
                        <span className="text-gray-400 text-xs">
                          {brand.email_dkim_verified ? 'Verified' : 'Add all three CNAME records below'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <VerificationIcon verified={brand.email_dmarc_verified} />
                        <span className="font-medium w-14">DMARC</span>
                        <span className="text-gray-400 text-xs">
                          {brand.email_dmarc_verified ? 'Verified' : 'Add the TXT record below'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <VerificationIcon verified={brand.email_spf_verified} />
                    Step 1: SPF Record
                  </h4>
                  <p className="text-xs text-gray-400 mb-2">
                    Authorizes our mail servers to send email on behalf of your domain.
                  </p>
                  <DnsRecord type="TXT" host={brand.email_domain} value="v=spf1 include:amazonses.com ~all" />
                  <p className="text-xs text-amber-600 dark:text-amber-400 mb-5 -mt-1">
                    If your domain already has an SPF record, don't create a second one — add{' '}
                    <code className="bg-amber-50 dark:bg-amber-900/30 px-1 rounded">include:amazonses.com</code> to your
                    existing record instead.
                  </p>

                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                    <VerificationIcon verified={brand.email_dkim_verified} />
                    Step 2: DKIM Records
                  </h4>
                  <p className="text-xs text-gray-400 mb-2">
                    Adds a cryptographic signature to outgoing emails, proving they haven't been tampered with.
                    Add all three CNAME records.
                  </p>
                  {brand.email_dkim_tokens?.map((token, i) => (
                    <DnsRecord
                      key={token}
                      type={`CNAME (${i + 1} of 3)`}
                      host={`${token}._domainkey.${brand.email_domain}`}
                      value={`${token}.dkim.amazonses.com`}
                    />
                  ))}

                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 mt-5 flex items-center gap-2">
                    <VerificationIcon verified={brand.email_dmarc_verified} />
                    Step 3: DMARC Record
                  </h4>
                  <p className="text-xs text-gray-400 mb-2">
                    Tells receiving mail servers to reject emails that fail SPF or DKIM checks, protecting your domain
                    from spoofing.
                  </p>
                  <DnsRecord type="TXT" host={`_dmarc.${brand.email_domain}`} value="v=DMARC1; p=reject;" />
                  <p className="text-xs text-gray-400 mb-4 -mt-1">
                    To receive aggregate DMARC reports, you can optionally append{' '}
                    <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                      {' '}rua=mailto:dmarc-reports@yourdomain.com
                    </code>{' '}
                    to the value.
                  </p>

                  <div className="flex gap-3 mt-6">
                    <VerdocsButton label="Verify DNS Records" size="small" onClick={handleVerifyEmailDomain} disabled={working} />
                    <VerdocsButton
                      label="Remove Sender"
                      size="small"
                      variant="outline"
                      onClick={() => setConfirmingRemoveEmail(true)}
                      disabled={working}
                    />
                  </div>

                  <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-[#33364b] dark:text-white mb-4">Delivery Metrics</h3>
                    <p className="text-sm text-gray-400 italic">Email delivery metrics will appear here once available.</p>
                  </div>
                </div>
              ) : showEmailSetup ? (
                <div className="mb-4">
                  <div className="flex flex-col gap-5">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        Sending Domain <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={emailSubdomain}
                        onChange={(e) => setEmailSubdomain(e.target.value.toLowerCase())}
                        placeholder="notify.acme.com"
                        className="max-w-sm w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        You can use your primary domain (e.g. <code>acme.com</code>) or a dedicated subdomain
                        (e.g. <code>notify.acme.com</code>). A dedicated subdomain is recommended because it isolates your notification
                        sending reputation from your main domain — if deliverability issues arise, they won't affect your regular business
                        email.
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        From Address <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-1 max-w-sm">
                        <input
                          type="text"
                          value={emailLocalPart}
                          onChange={(e) => setEmailLocalPart(e.target.value)}
                          placeholder="notifications"
                          className="flex-1 px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                        />
                        <span className="text-sm text-gray-400">@{emailSubdomain || '...'}</span>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Sender Name</label>
                      <input
                        type="text"
                        value={emailDisplayName}
                        onChange={(e) => setEmailDisplayName(e.target.value)}
                        placeholder="Acme Documents"
                        className="max-w-sm w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">The name recipients see in their inbox. Defaults to your brand name.</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Reply-to Address</label>
                      <input
                        type="email"
                        value={emailReplyTo}
                        onChange={(e) => setEmailReplyTo(e.target.value)}
                        placeholder="support@acme.com"
                        className="max-w-sm w-full px-3 py-2 text-sm border rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">Where replies go when a recipient responds to a notification.</p>
                    </div>
                  </div>

                  {emailSubdomain && emailLocalPart && (
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-w-sm">
                      <p className="text-xs text-gray-500 mb-1">Recipients will see:</p>
                      <p className="text-sm font-medium">
                        {emailDisplayName || 'Your Brand'} &lt;{emailLocalPart}@{emailSubdomain}&gt;
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 mt-6">
                    <VerdocsButton label="Set Up Sender" size="small" onClick={handleAddEmailDomain} disabled={working} />
                    <VerdocsButton label="Cancel" size="small" variant="outline" onClick={() => setShowEmailSetup(false)} disabled={working} />
                  </div>
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-4">
                    No custom sender configured. Notifications will be sent from the default Verdocs address.
                  </p>
                  <VerdocsButton label="Set Up Sender" size="small" onClick={() => setShowEmailSetup(true)} />
                </div>
              )}
            </div>
          )}

          {/* ── Documents Tab ── */}
          {activeTab === 'documents' && (
            <div className="pt-4 max-w-2xl">
              <div className="flex flex-row items-center mb-4">
                <h3 className="text-lg font-semibold text-[#33364b] dark:text-white">PDF Signature Branding</h3>
                <FlexFill />
                <HelpDialog title="PDF Branding">
                  <p className="mb-4">These values appear in the metadata of signed PDF documents.</p>
                  <p>"Certified By" and "Location" are shown in PDF readers when viewing the digital signature details.</p>
                </HelpDialog>
              </div>

              <FormColumns>
                <FormColumn>
                  <TextInput label="Certified By" name="pdf_signature_reason" placeholder="Certified By Verdocs" />
                  <TextInput label="Location" name="pdf_signature_location" placeholder="Verdocs" />
                </FormColumn>
                <FormColumn />
              </FormColumns>

              <h3 className="text-lg font-semibold text-[#33364b] dark:text-white mt-8 mb-4">Custom Disclaimer</h3>
              <textarea
                {...form.register('disclaimer')}
                rows={6}
                className="text-sm p-3 w-full border rounded-lg border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-white focus:border-[#654dcb] focus:ring-1 focus:ring-[#654dcb] outline-none"
                placeholder="Custom e-signing disclosure text shown to signers before they begin..."
              />
            </div>
          )}

          {/* ── Settings Tab ── */}
          {activeTab === 'settings' && (
            <div className="pt-4 max-w-md">
              <div className="mt-4 pt-6 border-t border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Deleting a brand cannot be undone. Envelopes associated with this brand will fall back to default Verdocs branding.
                </p>
                <VerdocsButton label="Delete Brand" size="small" variant="outline" onClick={() => setConfirmingDelete(true)} />
              </div>
            </div>
          )}

          {activeTab !== 'preview' && (
            <div className="mt-6">
              <VerdocsButton type="submit" label="Save" size="normal" disabled={form.formState.isSubmitting || !dirty} />
            </div>
          )}
        </form>
      </FormProvider>

      {/* Preview lives outside the form to prevent VerdocsSign buttons from triggering saves */}
      {/* Merge live form edits over the saved brand so the preview reflects in-progress settings, not just what's been saved. */}
      {activeTab === 'preview' && brand && <BrandPreview brand={{...brand, ...form.watch()} as IBrand} />}

      {confirmingDelete && (
        <OkCancelDialog onOk={handleDelete} onCancel={() => setConfirmingDelete(false)}>
          <p className="mb-6">
            Are you sure you want to delete <strong>{brand?.name || brand?.key}</strong>? This cannot be undone. Envelopes associated with this
            brand will fall back to default Verdocs branding.
          </p>
        </OkCancelDialog>
      )}

      {confirmingRemoveEmail && (
        <OkCancelDialog onOk={handleRemoveEmailDomain} onCancel={() => setConfirmingRemoveEmail(false)}>
          <p className="mb-6">
            Are you sure you want to remove the custom email domain? Notifications will revert to sending from the default Verdocs address.
          </p>
        </OkCancelDialog>
      )}

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}
    </div>
  );
};
